import React, { useEffect, useState } from "react";
import { api, ApiClientError } from "../api";
import type { Id, PaymentMethod, Sale } from "../types";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { usePath, useNavigate, Link, matchPath } from "../router";
import { formatCurrency, formatDateTime } from "../lib/format";

type PaymentCondition = {
  id: Id;
  code: string;
  name: string;
  provider: string | null;
  payment_method: PaymentMethod;
  installments: number;
  card_brand: string | null;
  bank_name: string | null;
  promotion_name: string | null;
  fee_pct: number | string;
  fee_vat_pct: number | string;
  additional_pct: number | string;
  fixed_fee: number | string;
};

const PAYMENT_METHODS: { code: PaymentMethod; label: string }[] = [
  { code: "TRANSFER", label: "Transferencia" },
  { code: "CASH", label: "Efectivo" },
  { code: "CARD_1_PAYMENT", label: "Tarjeta 1 pago" },
  { code: "CARD_UP_TO_6", label: "Tarjeta hasta 6 cuotas" },
];

export default function Ventas() {
  const path = usePath();
  const params = matchPath("/ventas/:id", path);
  if (params?.id) return <SaleDetail saleId={params.id} />;
  return <SalesList />;
}

function SalesList() {
  const { isAdminLike } = useAuth();
  const navigate = useNavigate();
  const [sales, setSales] = useState<Sale[] | null>(null);
  const [jumpId, setJumpId] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdminLike) return;
    api
      .getSales()
      .then(setSales)
      .catch((e) => {
        if (e instanceof ApiClientError) setError(e.message);
      });
  }, [isAdminLike]);

  return (
    <div className="ventas-page">
      <h1>Ventas</h1>
      <p className="meta">Cobros, devoluciones y estado de cada venta generada a partir de un presupuesto aceptado.</p>

      {!isAdminLike && (
        <div className="dashboard-section">
          <h2>Ir a una venta</h2>
          <p className="meta">
            Ingresá el ID de la venta (lo ves al aceptar un presupuesto en la ficha 360°) para registrar un cobro o
            consultar su estado.
          </p>
          <div className="row" style={{ gap: 8 }}>
            <input placeholder="ID de venta" value={jumpId} onChange={(e: any) => setJumpId(e.target.value)} style={{ width: 160 }} />
            <button className="btn-primary" disabled={!jumpId.trim()} onClick={() => navigate(`/ventas/${jumpId.trim()}`)}>
              Ver venta
            </button>
          </div>
        </div>
      )}

      {isAdminLike && (
        <div className="dashboard-section">
          {error && <p className="form-error">{error}</p>}
          <table className="task-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Producto</th>
                <th>Monto</th>
                <th>Cobrado</th>
                <th>Medio</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {(sales || []).map((s) => (
                <tr key={s.id} className="clickable-row" onClick={() => navigate(`/ventas/${s.id}`)}>
                  <td>#{s.id}</td>
                  <td>{s.product?.name || `#${s.product_id ?? "-"}`}</td>
                  <td>{formatCurrency(s.gross_amount)}</td>
                  <td>{formatCurrency(s.gross_collected)}</td>
                  <td>{paymentMethodLabel(s.payment_method)}</td>
                  <td>
                    <span className={`pill ${saleStatusClass(s.status)}`}>{s.status}</span>
                  </td>
                </tr>
              ))}
              {sales && sales.length === 0 && (
                <tr>
                  <td colSpan={6} className="meta">
                    Todavía no hay ventas registradas.
                  </td>
                </tr>
              )}
              {!sales && !error && (
                <tr>
                  <td colSpan={6} className="meta">
                    Cargando...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function paymentMethodLabel(method: PaymentMethod) {
  return PAYMENT_METHODS.find((m) => m.code === method)?.label || method;
}

function saleStatusClass(status: string) {
  if (["PAID", "COMPLETED"].includes(status)) return "pill-ok";
  if (["CANCELLED", "REFUNDED"].includes(status)) return "pill-bad";
  return "";
}

function SaleDetail({ saleId }: { saleId: Id }) {
  const { user, isAdminLike } = useAuth();
  const { show } = useToast();
  const [sale, setSale] = useState<Sale | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<PaymentMethod>("TRANSFER");
  const [paymentConditions, setPaymentConditions] = useState<PaymentCondition[]>([]);
  const [paymentConditionId, setPaymentConditionId] = useState("");
  const [installments, setInstallments] = useState("1");
  const [submitting, setSubmitting] = useState(false);

  const [refundPaymentId, setRefundPaymentId] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [refunding, setRefunding] = useState(false);

  function reload() {
    setLoading(true);
    api
      .getSale(saleId)
      .then((s) => {
        setSale(s);
        setError(null);

        setMethod(s.payment_method || "TRANSFER");
        setPaymentConditionId(
          s.payment_condition_id != null
            ? String(s.payment_condition_id)
            : ""
        );
      })
      .catch((e) => {
        setSale(null);
        setError(e instanceof ApiClientError ? e.message : "No se pudo cargar la venta.");
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    reload();

    api
      .getPaymentConditions({ active: true })
      .then(setPaymentConditions)
      .catch(() => {
        setPaymentConditions([]);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saleId]);

  useEffect(() => {
    if (!paymentConditionId) return;

    const selected = paymentConditions.find(
      (condition) => String(condition.id) === String(paymentConditionId)
    );

    if (!selected) return;

    if (selected.payment_method !== method) {
      setPaymentConditionId("");
      setInstallments("1");
      return;
    }

    setInstallments(String(selected.installments || 1));
  }, [paymentConditionId, paymentConditions, method]);

  function changePaymentMethod(nextMethod: PaymentMethod) {
    setMethod(nextMethod);

    const selected = paymentConditions.find(
      (condition) => String(condition.id) === String(paymentConditionId)
    );

    if (!selected || selected.payment_method !== nextMethod) {
      setPaymentConditionId("");
      setInstallments("1");
    }
  }

  function changePaymentCondition(id: string) {
    setPaymentConditionId(id);

    if (!id) return;

    const selected = paymentConditions.find(
      (condition) => String(condition.id) === String(id)
    );

    if (selected) {
      setMethod(selected.payment_method);
      setInstallments(String(selected.installments || 1));
    }
  }

  async function registerPayment() {
    const n = Number(amount);
    if (!n || n <= 0) {
      show("Ingresá un monto válido.", "error");
      return;
    }
    setSubmitting(true);
    try {
      await api.registerPayment(saleId, {
        amount: n,
        payment_method: method,
        payment_condition_id: paymentConditionId || undefined,
        installments: Number(installments) || 1,
      });
      show("Cobro registrado.", "success");
      setAmount("");
      reload();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo registrar el cobro.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function registerRefund() {
    const n = Number(refundAmount);
    if (!refundPaymentId || !n || n <= 0) {
      show("Elegí el cobro original e ingresá un monto.", "error");
      return;
    }
    setRefunding(true);
    try {
      await api.registerRefund(saleId, { payment_id: refundPaymentId, amount: n, reason: refundReason || undefined });
      show("Devolución registrada.", "success");
      setRefundPaymentId("");
      setRefundAmount("");
      setRefundReason("");
      reload();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo registrar la devolución.", "error");
    } finally {
      setRefunding(false);
    }
  }

  async function cancelSale() {
    if (!confirm("¿Cancelar esta venta?")) return;
    try {
      await api.cancelSale(saleId);
      show("Venta cancelada.", "success");
      reload();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo cancelar la venta.", "error");
    }
  }

  if (loading && !sale) return <div className="ventas-page"><p className="meta">Cargando venta...</p></div>;
  if (error) return <div className="ventas-page"><Link to="/ventas">← Volver a ventas</Link><p className="form-error">{error}</p></div>;
  if (!sale) return null;

  const charges = (sale.snapshots || []).filter((s) => s.movement_type === "charge");
  const refunds = (sale.snapshots || []).filter((s) => s.movement_type === "refund");
  const outstandingBalance = Math.max(
    0,
    Number(sale.gross_amount || 0) - Number(sale.gross_collected || 0)
  );

  const canRegisterPayment =
    outstandingBalance > 0 &&
    !["PAID", "COMPLETED", "CANCELLED", "REFUNDED"].includes(sale.status);

  return (
    <div className="ventas-page">
      <Link to="/ventas">← Volver a ventas</Link>
      <h1>Venta #{sale.id}</h1>
      <div className="row" style={{ gap: 16, flexWrap: "wrap" }}>
        <div className="kpi">
          <div>
            Estado <b className={saleStatusClass(sale.status) === "pill-bad" ? "overdue" : ""}>{sale.status}</b>
          </div>
          <div>
            Monto total <b>{formatCurrency(sale.gross_amount)}</b>
          </div>
          <div>
            Cobrado neto <b>{formatCurrency(sale.gross_collected)}</b>
          </div>
          <div>
            Saldo pendiente <b>{formatCurrency(outstandingBalance)}</b>
          </div>
        </div>
      </div>

      {sale.seller_commission_estimate !== undefined && (
        <div className="dashboard-section">
          <h2>Tu comisión estimada</h2>
          <div className="big-number">{formatCurrency(sale.seller_commission_estimate)}</div>
        </div>
      )}

      {isAdminLike && sale.bcm !== undefined && (
        <div className="dashboard-section">
          <h2>Economía de la venta</h2>
          <div className="dashboard-metrics">
            <div className="metric-tile" style={{ cursor: "default" }}>
              <div className="label">BCM</div>
              <div className="value">{formatCurrency(sale.bcm)}</div>
            </div>
            <div className="metric-tile" style={{ cursor: "default" }}>
              <div className="label">Comisión vendedora</div>
              <div className="value">{formatCurrency(sale.seller_commission)}</div>
            </div>
            <div className="metric-tile" style={{ cursor: "default" }}>
              <div className="label">Fondo de integración</div>
              <div className="value">{formatCurrency(sale.integration_fund)}</div>
            </div>
            <div className="metric-tile" style={{ cursor: "default" }}>
              <div className="label">Contribución Mauvelore</div>
              <div className="value">{formatCurrency(sale.mauvelore_contribution)}</div>
            </div>
          </div>
        </div>
      )}

      {sale.referral_points_awarded !== undefined && (
        <div className="dashboard-section">
          <h2>Derivación</h2>
          <p>{sale.referral_points_awarded ? "Esta venta otorgó puntos de derivación." : "Sin puntos otorgados todavía."}</p>
        </div>
      )}

      <div className="dashboard-section">
        <h2>Historial de cobros</h2>
        <table className="task-table">
          <thead>
            <tr>
              <th>Pago</th>
              <th>Tipo</th>
              <th>Monto</th>
              <th>Fecha</th>
            </tr>
          </thead>
          <tbody>
            {charges.map((c) => (
              <tr key={c.id}>
                <td>#{c.payment_id}</td>
                <td>Cobro</td>
                <td>{formatCurrency(c.gross_collected)}</td>
                <td>{formatDateTime(c.created_at)}</td>
              </tr>
            ))}
            {refunds.map((r) => (
              <tr key={r.id}>
                <td>#{r.payment_id}</td>
                <td className="overdue">Devolución</td>
                <td className="overdue">{formatCurrency(r.gross_collected)}</td>
                <td>{formatDateTime(r.created_at)}</td>
              </tr>
            ))}
            {charges.length === 0 && refunds.length === 0 && (
              <tr>
                <td colSpan={4} className="meta">
                  Todavía no hay cobros registrados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {canRegisterPayment && (
        <div className="dashboard-section">
          <h2>Registrar cobro</h2>
          <div className="form-grid" style={{ maxWidth: 360 }}>
            <label>Monto</label>
            <input type="number" value={amount} onChange={(e: any) => setAmount(e.target.value)} />
            <label>Medio de pago</label>
            <select
              value={method}
              onChange={(e: any) =>
                changePaymentMethod(e.target.value as PaymentMethod)
              }
            >
              {PAYMENT_METHODS.map((m) => (
                <option key={m.code} value={m.code}>
                  {m.label}
                </option>
              ))}
            </select>

            <label>Condición de cobro</label>
            <select
              value={paymentConditionId}
              onChange={(e: any) =>
                changePaymentCondition(e.target.value)
              }
            >
              <option value="">
                Sin condición específica · usar configuración estándar
              </option>

              {paymentConditions
                .filter(
                  (condition) =>
                    condition.payment_method === method
                )
                .map((condition) => {
                  const details = [
                    condition.provider,
                    condition.installments > 1
                      ? `${condition.installments} cuotas`
                      : "1 pago",
                    condition.promotion_name,
                    condition.bank_name,
                    condition.card_brand,
                  ].filter(Boolean);

                  return (
                    <option
                      key={condition.id}
                      value={String(condition.id)}
                    >
                      {condition.name}
                      {details.length
                        ? ` · ${details.join(" · ")}`
                        : ""}
                    </option>
                  );
                })}
            </select>

            <label>Cuotas</label>
            <input
              type="number"
              min={1}
              value={installments}
              disabled={Boolean(paymentConditionId)}
              onChange={(e: any) =>
                setInstallments(e.target.value)
              }
            />

            {paymentConditionId && (
              <>
                <span />
                <p className="meta">
                  Las cuotas se toman automáticamente de la condición de cobro seleccionada.
                </p>
              </>
            )}
          </div>
          <button className="btn-primary" style={{ marginTop: 10 }} disabled={submitting} onClick={registerPayment}>
            {submitting ? "Registrando..." : "Registrar cobro"}
          </button>
        </div>
      )}

      {isAdminLike && charges.length > 0 && (
        <div className="dashboard-section">
          <h2>Registrar devolución</h2>
          <div className="form-grid" style={{ maxWidth: 360 }}>
            <label>Cobro original</label>
            <select value={refundPaymentId} onChange={(e: any) => setRefundPaymentId(e.target.value)}>
              <option value="">Elegí un cobro...</option>
              {charges.map((c) => (
                <option key={c.payment_id} value={c.payment_id}>
                  #{c.payment_id} — {formatCurrency(c.gross_collected)}
                </option>
              ))}
            </select>
            <label>Monto a devolver</label>
            <input type="number" value={refundAmount} onChange={(e: any) => setRefundAmount(e.target.value)} />
            <label>Motivo</label>
            <input value={refundReason} onChange={(e: any) => setRefundReason(e.target.value)} />
          </div>
          <button className="btn-ghost" style={{ marginTop: 10 }} disabled={refunding} onClick={registerRefund}>
            {refunding ? "Registrando..." : "Registrar devolución"}
          </button>
        </div>
      )}

      {isAdminLike && !["PAID", "COMPLETED", "REFUNDED", "CANCELLED"].includes(sale.status) && (
        <div className="dashboard-section">
          <button className="btn-ghost" onClick={cancelSale}>
            Cancelar venta
          </button>
        </div>
      )}
    </div>
  );
}
